import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, EmptyState, Spinner } from "@/components/ui";
import { fmtDateTime } from "@/lib/helpers";
import { MonitorSmartphone, Smartphone, Monitor } from "lucide-react";
import { toast } from "sonner";

export default function Sessions() {
  const [items, setItems] = useState(null);

  const load = useCallback(async () => { const r = await api.listSessions(); setItems(r.items || []); }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    try { await api.revokeSession(id); await load(); toast.success("Session revoked"); }
    catch (e) { toast.error(e?.detail || "Could not revoke"); }
  };
  const revokeOthers = async () => {
    try { const r = await api.revokeOtherSessions(); await load(); toast.success(`${r.revoked} other session(s) revoked`); }
    catch (e) { toast.error("Could not revoke"); }
  };

  if (!items) return <Spinner />;
  const active = items.filter((s) => !s.revoked);
  const hasOthers = active.filter((s) => !s.is_current).length > 0;

  return (
    <div className="kb-fade-up">
      <PageHeader eyebrow="Security" title="Session activity"
        action={hasOthers && <Button variant="secondary" data-testid="revoke-others" onClick={revokeOthers}>Sign out others</Button>} />

      {items.length === 0 ? (
        <Card><EmptyState testid="sessions-empty" icon={MonitorSmartphone} title="No sessions" body="Sessions appear here when you sign in on a device." /></Card>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <Card key={s.id} data-testid={`session-${s.id}`} className={s.revoked ? "opacity-50" : ""}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-tint flex items-center justify-center shrink-0">
                  {s.platform === "web" ? <Monitor className="w-5 h-5 text-brand-dark" /> : <Smartphone className="w-5 h-5 text-brand-dark" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-ink truncate">{s.device_label}</span>
                    {s.is_current && <span className="text-[10px] font-bold text-white bg-ok rounded-full px-1.5 py-0.5">THIS DEVICE</span>}
                    {s.revoked && <span className="text-[10px] font-bold text-subtle bg-line rounded-full px-1.5 py-0.5">REVOKED</span>}
                  </div>
                  <div className="text-xs text-subtle capitalize">{s.platform} · {s.auth_method.replace(/_/g, " ")} · last seen {fmtDateTime(s.last_seen_at)}</div>
                </div>
                {!s.revoked && !s.is_current && (
                  <button data-testid={`revoke-${s.id}`} onClick={() => revoke(s.id)} className="text-sm font-semibold text-danger hover:underline">Revoke</button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
